'use client';

import { FileText, ArrowRight, Download, AlertCircle, ShieldCheck, Trash2 } from 'lucide-react';
import { useState, useMemo, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackFunnelEvent } from '@/lib/analytics';
import type { DCMAScoreResult } from '@/lib/dcma-scoring-engine';
import { generateExecutiveSummary } from '@/lib/executive-summary';
import { submitLeadToHubSpot } from '@/lib/hubspot';
import { generateDCMAReport } from '@/lib/pdf-report';

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.co.in',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'mail.com',
  'email.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'zohomail.com',
  'yandex.com',
  'yandex.ru',
  'gmx.com',
  'gmx.net',
  'tutanota.com',
  'tuta.io',
  'fastmail.com',
  'hey.com',
  'mailinator.com',
  'guerrillamail.com',
  'tempmail.com',
]);

function validateEmail(email: string): { valid: boolean; error: string | null } {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return { valid: false, error: null };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) return { valid: false, error: 'Please enter a valid email address.' };

  const domain = trimmed.split('@')[1];
  if (FREE_EMAIL_DOMAINS.has(domain)) {
    return {
      valid: false,
      error: 'Please use your work email. Free email providers are not accepted.',
    };
  }

  return { valid: true, error: null };
}

export type Lead = {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
};

interface LeadCaptureFormProps {
  grade: string;
  projectName: string;
  scoreResult: DCMAScoreResult;
  // Called once the visitor submits valid details, so the parent can reveal the
  // full report inline.
  onUnlock: (lead: Lead) => void;
  // Optional HubSpot portal ID and form GUID. When both are provided, the lead
  // is submitted to HubSpot's Forms Submission API on submit.
  hubspotPortalId?: string;
  hubspotFormId?: string;
}

function downloadReportPdf(scoreResult: DCMAScoreResult, lead: Lead) {
  const execSummary = generateExecutiveSummary(scoreResult);
  const pdfBytes = generateDCMAReport(scoreResult, lead, execSummary);
  const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DCMA-Report-${scoreResult.projectName.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export function LeadCaptureForm({
  projectName,
  scoreResult,
  onUnlock,
  hubspotPortalId,
  hubspotFormId,
}: LeadCaptureFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Lead>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
  });

  const emailValidation = useMemo(() => validateEmail(formData.email), [formData.email]);
  const [emailTouched, setEmailTouched] = useState(false);
  const [companyTouched, setCompanyTouched] = useState(false);
  const formStartedRef = useRef(false);

  const isValid =
    formData.firstName.trim().length > 0 &&
    formData.lastName.trim().length > 0 &&
    emailValidation.valid &&
    formData.company.trim().length > 0;

  // Report the first interaction with any form field, once per mount.
  const handleFormFocus = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackFunnelEvent('dcma_form_started', { grade: scoreResult.grade });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setIsSubmitting(true);
    setError(null);

    // Forward the lead to HubSpot when configured. The analysis itself never
    // leaves the browser; only the contact details and computed result are
    // sent, and only when HubSpot is configured. A HubSpot hiccup never blocks
    // the visitor's report: on failure we still unlock and just record the
    // error event.
    if (hubspotPortalId && hubspotFormId) {
      try {
        await submitLeadToHubSpot({
          portalId: hubspotPortalId,
          formId: hubspotFormId,
          lead: formData,
          scoreResult,
        });
        trackFunnelEvent('dcma_form_submit', { grade: scoreResult.grade, score: scoreResult.totalScore });
      } catch {
        trackFunnelEvent('dcma_form_error', { grade: scoreResult.grade });
      }
    }

    onUnlock(formData);
    setSubmitted(true);
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <Card className="border-primary/20 bg-primary/[0.03]">
        <CardContent className="flex flex-col items-center gap-5 py-10 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <FileText className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Your Report is Ready</h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground leading-relaxed">
              The full DCMA analysis for <span className="font-medium text-foreground">{projectName}</span> is unlocked
              below. It includes an executive summary, criterion-by-criterion scoring, flagged activities, and your
              complete score breakdown.
            </p>
          </div>
          <Button size="lg" className="gap-2 mt-1" onClick={() => downloadReportPdf(scoreResult, formData)}>
            Download PDF Report
            <Download className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 mt-2 rounded-md bg-muted px-3 py-2">
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Your uploaded schedule file has been discarded and was never stored on our servers.
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-brand-orange/20 shadow-lg">
      <CardContent className="py-8">
        <div className="mx-auto max-w-md">
          <div className="text-center mb-6 space-y-3">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10">
              <FileText className="h-6 w-6 text-brand-orange" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Unlock Your Full Report</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Enter your email to unlock your complete DCMA assessment with executive summary, criterion-by-criterion
                analysis, flagged activities, and actionable recommendations
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={e => void handleSubmit(e)} onFocus={handleFormFocus} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="lead-first-name" className="text-sm">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="lead-first-name"
                  placeholder="Jane"
                  value={formData.firstName}
                  onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lead-last-name" className="text-sm">
                  Last Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="lead-last-name"
                  placeholder="Smith"
                  value={formData.lastName}
                  onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead-email" className="text-sm">
                Work Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lead-email"
                type="email"
                placeholder="jane@company.com"
                value={formData.email}
                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                onBlur={() => setEmailTouched(true)}
                required
                className={
                  emailTouched && emailValidation.error ? 'border-destructive focus-visible:ring-destructive/30' : ''
                }
              />
              {emailTouched && emailValidation.error && (
                <p className="text-xs text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  {emailValidation.error}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead-company" className="text-sm">
                Company <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lead-company"
                placeholder="Acme Construction"
                value={formData.company}
                onChange={e => setFormData(prev => ({ ...prev, company: e.target.value }))}
                onBlur={() => setCompanyTouched(true)}
                required
                className={
                  companyTouched && !formData.company.trim()
                    ? 'border-destructive focus-visible:ring-destructive/30'
                    : ''
                }
              />
              {companyTouched && !formData.company.trim() && (
                <p className="text-xs text-destructive flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  Company name is required.
                </p>
              )}
            </div>

            <Button type="submit" className="w-full gap-2" disabled={!isValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                  Generating Report...
                </>
              ) : (
                <>
                  Generate My Report
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/70 pt-1">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Your schedule file was analyzed locally and has already been discarded.</span>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}
